import { type CSSProperties, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { InventoryLevel } from '@interfaces/ILogistics';
import type { ISiteMapArea } from '@interfaces/ISiteMap';
import { Drawer } from '@components/ui/Drawer/Drawer';
import { DrawerCompanion } from '@components/ui/Drawer/DrawerCompanion';
import { useCompanionState } from '@components/ui/Drawer/useCompanionState';
import { DiscardModal } from '@components/ui/DiscardModal/DiscardModal';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { SearchInput } from '@components/ui/SearchInput/SearchInput';
import { KebabButton } from '@components/ui/KebabButton/KebabButton';
import { Menu } from '@components/ui/Menu/Menu';
import { ChevronDownIcon, PlusIcon } from '@utils/icons';
import { fold } from '../../units';
import { useInlineRename } from '../../useInlineRename';
import { InventoryRowMain, InventoryRowGauge } from '../InventoryRowCore';
import { useLogisticsEditLock } from '../../revision/LogisticsEditLock';
import { rowActivationProps } from '../rowActivation';
import { StockDetailBody, type StockFormPlumbing } from '../StockDetailDrawer';
import { AREA_COLOR_TOKEN, AREA_ICONS } from './siteMap.constants';
import { joinAreaStock, type IAreaStockRow } from './siteMap.logic';
import styles from './AreaDrawer.module.css';
import inventoryStyles from '../InventoryLevels.module.css';

export interface IAreaDrawerProps {
  /** The area to show. During a close this may be the caller's HELD copy —
   *  see `ui/Drawer`'s own note on staying mounted through the exit. */
  area: ISiteMapArea | null;
  open: boolean;
  /** Every area on the current map (this one included) — the add-stock
   *  picker uses this to look up whether a candidate material is already
   *  claimed by ANOTHER area, since a material may live in only one area
   *  at a time (the backend refuses a write that would break it, too). */
  areas: ISiteMapArea[];
  /** The site's current Inventory Levels — `joinAreaStock` resolves the
   *  area's `materials` (name references) against these. */
  levels: InventoryLevel[];
  /** The PROJECT's IANA timezone — the stock rows' Started/Lead dates read
   *  through it (same as `InventoryRow`). The row list is omitted entirely
   *  while this is undefined — never defaulted to a fixed zone. */
  tz: string | undefined;
  onClose: () => void;
  /** The stock form's inputs, for the stock companion's Edit tab — the same
   *  plumbing the page hands the stock dialog and the stock drawer. */
  stockPlumbing: StockFormPlumbing;
  /** Stages a material delete from the companion's Edit tab — the page's
   *  DiscardModal fires it, same as the stock drawer's own Delete. */
  onDeleteStock: (level: InventoryLevel) => void;
  onRename: (name: string) => void;
  /** Adds a material to the area — called with the FULL next materials list
   *  (existing + the newly picked name), never a delta. */
  onAssignMaterials: (materials: string[]) => void;
  onDelete: () => void;
  /** The caller's role may write (the page's `canEdit`). When false the kebab
   *  (rename / delete) and the add-stock picker are not rendered, and the
   *  stock companion has no Edit tab. Reading the area stays live. */
  canEdit: boolean;
}

/**
 * The site-map area drawer: joined stock (Task 5's `joinAreaStock`), an
 * in-drawer add-stock picker over the site's
 * Inventory Levels, and rename/delete. Stays MOUNTED across an open/close
 * cycle — see `ui/Drawer` — so Base UI plays the slide-out on the last area
 * shown rather than the panel snapping shut on a null area.
 *
 * Owns no mutations: rename, add-stock and delete are all raised to the
 * caller (mirrors `DeliveryDetailDrawer`), which owns `useSiteMapData`'s
 * `updateArea`/`removeArea` mutations. Delete only STAGES here — the
 * `DiscardModal` confirm is what actually fires, same as every other
 * irreversible delete on this page.
 */
export function AreaDrawer({
  area,
  open,
  areas,
  levels,
  tz,
  onClose,
  stockPlumbing,
  onDeleteStock,
  onRename,
  onAssignMaterials,
  onDelete,
  canEdit,
}: IAreaDrawerProps) {
  const { t } = useTranslation();
  /* The stock companion (a `DrawerCompanion` docked left, the issue drawer's
     "set locations" idiom): a tapped stock row opens the SAME record body
     the stock drawer shows. `useCompanionState` owns the open/mounted
     choreography and the HELD level that keeps the exit populated; keyed on
     the area so a companion opened on one never leaks into the next. Lives
     here (not in the keyed body) because the companion is the Drawer's own
     slot. */
  const stock = useCompanionState<InventoryLevel>(area?.areaId ?? null);
  // The LIVE record for the companion (a save's refetch updates it in
  // place); the held copy only carries the exit after a delete.
  const held = stock.record;
  const currentStockLevel = held ? levels.find((l) => fold(l.materialName) === fold(held.materialName)) : null;
  const liveStockLevel = currentStockLevel ?? stock.record;
  // A deleted material closes its companion (render-time adjust, like the
  // key reset inside the hook) — the held copy stays only for the slide-out.
  // Without this the stale record would sit open forever, its Save able to
  // re-POST the deleted material back into existence.
  if (stock.open && stock.record && !currentStockLevel) stock.close();
  return (
    <Drawer
      open={open && area !== null}
      ariaLabel={area ? area.name : t('logistics.zones.title')}
      closeLabel={t('common.close')}
      onClose={onClose}
      companion={
        stock.mounted && liveStockLevel && tz ? (
          <DrawerCompanion
            open={stock.open}
            onClose={stock.close}
            onExited={stock.unmount}
            ariaLabel={`${t('logistics.stockDetail.title')} ${liveStockLevel.materialName}`}
            closeLabel={t('logistics.stockDetail.close')}
          >
            <StockDetailBody
              /* Keyed per material like the stock drawer's own body — a tap
                 on a DIFFERENT row must reset the tabs to Overview, not
                 inherit the previous record's Edit state. */
              key={liveStockLevel.materialName}
              level={liveStockLevel}
              tz={tz}
              onClose={stock.close}
              onDelete={onDeleteStock}
              canEdit={canEdit}
              {...stockPlumbing}
            />
          </DrawerCompanion>
        ) : null
      }
    >
      {/* Body lives INSIDE the popup, keyed by the area, so every per-area
          state it holds (rename draft, picker open/query) dies with Base
          UI's post-exit unmount and can never leak into the next area. */}
      {area ? (
        <AreaDrawerBody
          key={area.areaId}
          area={area}
          areas={areas}
          levels={levels}
          tz={tz}
          onRename={onRename}
          onAssignMaterials={onAssignMaterials}
          onDelete={onDelete}
          onOpenStock={stock.openWith}
          canEdit={canEdit}
        />
      ) : null}
    </Drawer>
  );
}

type IAreaDrawerBodyProps = Omit<
  IAreaDrawerProps,
  'open' | 'onClose' | 'area' | 'stockPlumbing' | 'onDeleteStock'
> & {
  area: ISiteMapArea;
  /** A tapped stock row raises its record — the AreaDrawer opens the
   *  companion with it. */
  onOpenStock: (level: InventoryLevel) => void;
};

function AreaDrawerBody({
  area,
  areas,
  levels,
  tz,
  onRename,
  onAssignMaterials,
  onDelete,
  onOpenStock,
  canEdit,
}: IAreaDrawerBodyProps) {
  const { t } = useTranslation();
  const [renaming, setRenaming] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const rows = useMemo(() => joinAreaStock(area.materials, levels), [area, levels]);
  const Icon = AREA_ICONS[area.icon];

  return (
    <>
      <header className={styles.head}>
        <div className={styles.identity}>
          <span
            className={styles.iconTint}
            style={{ '--tint': AREA_COLOR_TOKEN[area.color] } as CSSProperties}
          >
            <Icon size={18} />
          </span>
          <div className={styles.titleBlock}>
            {renaming ? (
              <RenameField
                initial={area.name}
                onCommit={(name) => {
                  onRename(name);
                  setRenaming(false);
                }}
                onCancel={() => setRenaming(false)}
              />
            ) : (
              <h2 className={styles.title}>{area.name}</h2>
            )}
            <span className={styles.meta}>
              {t('logistics.siteMap.drawer.pointsMeta', {
                points: area.points.length,
                materials: area.materials.length,
              })}
            </span>
          </div>
        </div>
        {canEdit && (
          <AreaActionsMenu
            stockCount={rows.length}
            onRename={() => setRenaming(true)}
            onDelete={() => setConfirmingDelete(true)}
          />
        )}
      </header>

      <section className={styles.stockSection}>
        <h3 className={styles.sectionTitle}>{t('logistics.siteMap.drawer.stockTitle')}</h3>
        {rows.length === 0 ? (
          <p className={styles.empty}>{t('logistics.siteMap.drawer.noStock')}</p>
        ) : (
          <>
            {/* Each row mirrors the Inventory Levels card row exactly, via
                `InventoryRowCore` — no reorder/delivery hint lines or
                order/jump buttons (those stay on the Inventory Levels card);
                tapping a row opens its record in the stock companion. `tz`
                is only undefined while the working site is still resolving
                (frontend/CLAUDE.md invariant #6: gate, never default) — the
                list simply waits for it. */}
            {tz ? (
              <ul className={styles.stockList}>
                {rows.map((row) => (
                  <StockRow key={row.materialName} row={row} tz={tz} onOpen={onOpenStock} />
                ))}
              </ul>
            ) : null}
            <p className={styles.syncNote}>{t('logistics.siteMap.drawer.syncNote')}</p>
          </>
        )}
      </section>

      {canEdit && (
        <AreaStockPicker area={area} areas={areas} levels={levels} onAssignMaterials={onAssignMaterials} />
      )}

      <DiscardModal
        open={confirmingDelete}
        onCancel={() => setConfirmingDelete(false)}
        onConfirm={() => {
          onDelete();
          setConfirmingDelete(false);
        }}
        title={t('logistics.siteMap.drawer.deleteArea')}
        description={t('logistics.siteMap.drawer.deleteConfirm')}
      />
    </>
  );
}

interface IAreaActionsMenuProps {
  /** How many resolved stock rows the area holds. */
  stockCount: number;
  onRename: () => void;
  onDelete: () => void;
}

/** The header's ⋯ menu — rename / delete. Rendered only for a writer;
 *  disabled while the page's edit lock holds. */
function AreaActionsMenu({ stockCount, onRename, onDelete }: IAreaActionsMenuProps) {
  const { t } = useTranslation();
  const { locked } = useLogisticsEditLock();
  /* An area still HOLDING stock cannot be deleted (design 2026-09-02, option
     A): the menu item fades and stops responding, and the why sits right
     under it in red — the count plus the way out. The count is the RESOLVED
     rows, never the raw `materials` refs: a ghost reference (its material
     deleted from Inventory) renders "No stock assigned yet." above, and the
     gate must agree with what the drawer shows. An empty area keeps the red
     item → confirm flow. Deliberately out of scope: the map's ⋯ "Clear
     map…" still wipes every area wholesale — the guard is for the
     one-at-a-time delete. The why is a SIBLING of the item (unlike
     `StatusesMenu`'s muted in-item span) so the red reason keeps full
     strength under the item's fade. */
  const deleteBlocked = stockCount > 0;
  const deleteWhyId = useId();
  return (
    <Menu.Root>
      <Menu.Trigger
        disabled={locked}
        render={
          /* Same 18px glyph as `ui/Drawer`'s own corner close button so the
             two hit-areas match — see `.kebabBtn` for the vertical nudge. */
          <KebabButton className={styles.kebabBtn} iconSize={18} aria-label={t('logistics.siteMap.more')} />
        }
      />
      <Menu.Portal>
        {/* `ui/Menu`'s own positioner sits at `--z-popover` (100) — fine
            for a menu in normal page flow, but this one is anchored
            INSIDE the Drawer's dialog layer (`--z-dialog`, 1000), so at
            the shared z-index the popup paints underneath the drawer
            panel and is invisible. Lift it to the token the design
            system reserves for exactly this case (see `DateField` /
            `SingleSelect` / `GroupedSelect`'s `elevated` prop for the
            same override elsewhere) — an inline style since it's a
            one-off, per-instance override of a shared component's rule,
            not a change to `ui/Menu` itself. */}
        <Menu.Positioner
          side="bottom"
          align="end"
          sideOffset={4}
          style={{ zIndex: 'var(--z-dialog-popover)' } as CSSProperties}
        >
          <Menu.Popup>
            <Menu.Item onClick={onRename}>
              {t('logistics.siteMap.drawer.rename')}
            </Menu.Item>
            <Menu.Item
              className={styles.deleteItem}
              disabled={deleteBlocked}
              aria-describedby={deleteBlocked ? deleteWhyId : undefined}
              onClick={onDelete}
            >
              {t('logistics.siteMap.drawer.deleteArea')}
            </Menu.Item>
            {deleteBlocked ? (
              <p id={deleteWhyId} className={styles.deleteWhy}>
                {t('logistics.siteMap.drawer.deleteBlocked', { count: stockCount })}
              </p>
            ) : null}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}

type IAreaStockPickerProps = Pick<IAreaDrawerProps, 'areas' | 'levels' | 'onAssignMaterials'> & {
  area: ISiteMapArea;
};

/** The "Add stock" picker under the stock list. Rendered only for a writer;
 *  its toggle is disabled while the page's edit lock holds. */
function AreaStockPicker({ area, areas, levels, onAssignMaterials }: IAreaStockPickerProps) {
  const { t } = useTranslation();
  const { locked } = useLogisticsEditLock();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState('');
  const assignedFold = useMemo(() => new Set(area.materials.map(fold)), [area]);
  // A material can live in only ONE area — every OTHER area's materials,
  // folded, mapped to that area's name. Enforced here at the interaction
  // layer, and by the backend (a 409 `material-in-other-area`) for a second
  // tab or a direct API write.
  const assignedElsewhere = useMemo(() => {
    const map = new Map<string, string>();
    for (const other of areas) {
      if (other.areaId === area.areaId) continue;
      for (const materialName of other.materials) {
        const key = fold(materialName);
        if (!map.has(key)) map.set(key, other.name);
      }
    }
    return map;
  }, [area, areas]);
  const trimmedQuery = fold(query.trim());
  const pickerLevels = trimmedQuery
    ? levels.filter((l) => fold(l.materialName).includes(trimmedQuery))
    : levels;

  return (
    <section className={styles.picker}>
      <button
        type="button"
        className={styles.pickerToggle}
        onClick={() => setPickerOpen((v) => !v)}
        aria-expanded={pickerOpen}
        disabled={locked}
      >
        <span className={styles.caret} data-collapsed={!pickerOpen}>
          <ChevronDownIcon size={14} />
        </span>
        {t('logistics.siteMap.drawer.addStock')}
      </button>
      {pickerOpen ? (
        <div className={styles.pickerBody}>
          <SearchInput
            value={query}
            onChange={setQuery}
            placeholder={t('logistics.siteMap.drawer.searchInventory')}
            ariaLabelClear={t('logistics.materials.searchClear')}
            className={styles.pickerSearch}
          />
          <ScrollArea className={styles.pickerScroll}>
            <ul className={styles.pickerList} data-testid="area-picker-list">
              {pickerLevels.map((level) => {
                const assigned = assignedFold.has(fold(level.materialName));
                const otherAreaName = assignedElsewhere.get(fold(level.materialName));
                return (
                  <li key={level.materialName} className={styles.pickerRow}>
                    <span className={styles.pickerName}>{level.materialName}</span>
                    {assigned ? (
                      <span className={styles.tag}>{t('logistics.siteMap.drawer.inThisArea')}</span>
                    ) : otherAreaName ? (
                      <span className={styles.tag}>
                        {t('logistics.siteMap.drawer.inOtherArea', { area: otherAreaName })}
                      </span>
                    ) : (
                      <button
                        type="button"
                        className={styles.addBtn}
                        aria-label={level.materialName}
                        onClick={() => onAssignMaterials([...area.materials, level.materialName])}
                      >
                        <PlusIcon size={12} />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </ScrollArea>
        </div>
      ) : null}
    </section>
  );
}

/** One joined stock row, rendered EXACTLY like an Inventory Levels card row
 *  (`InventoryRowMain` + `InventoryRowGauge`) — no reorder/delivery hint
 *  lines (those actions stay on the Inventory Levels card). Tapping it opens
 *  the record in the stock companion, the same one-affordance rule — and the
 *  same shared `rowActivationProps` idiom — the Inventory Levels rows follow. */
function StockRow({
  row,
  tz,
  onOpen,
}: {
  row: IAreaStockRow;
  tz: string;
  onOpen: (level: InventoryLevel) => void;
}) {
  const { level } = row;
  return (
    <li
      className={inventoryStyles.row}
      {...rowActivationProps(() => onOpen(level), level.materialName)}
      data-needscheck={level.needsCheck || undefined}
    >
      <div className={inventoryStyles.main}>
        <InventoryRowMain level={level} tz={tz} />
      </div>
      <InventoryRowGauge level={level} tz={tz} />
    </li>
  );
}

interface IRenameFieldProps {
  initial: string;
  onCommit: (name: string) => void;
  onCancel: () => void;
}

/** Tiny inline editor swapped in for the title. The commit/cancel/Escape
 *  mechanics live in `useInlineRename` (shared with `StatusesMenu`'s own
 *  rename field) — this only supplies the header's markup. Sharing the hook
 *  matters beyond dedup: `useInlineRename`'s Escape handler stops the
 *  keydown from bubbling, which is what keeps cancelling this edit from
 *  also closing the surrounding Drawer (Base UI's `Dialog` listens for
 *  Escape at the document level). */
function RenameField({ initial, onCommit, onCancel }: IRenameFieldProps) {
  const { t } = useTranslation();
  const { value, setValue, handleKeyDown, handleBlur } = useInlineRename({
    initial,
    onCommit,
    onCancel,
  });

  return (
    <input
      autoFocus
      type="text"
      className={styles.renameInput}
      aria-label={t('logistics.siteMap.drawer.rename')}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
    />
  );
}
